import React from 'react';
import Link from 'next/link';
import { useNotificationStore } from '@stores/NotificationStore';
import WritingResumeBtn from './resumeBtn/WritingResumeBtn';
import SubmitResumeBtn from './resumeBtn/SubmitResumeBtn';
import ProfileResumeBtn from './resumeBtn/ProfileResumeBtn';
import MatchingProfileResumeViewBtn from './resumeBtn/MatchingProfileResumeBtn';
import MatchingReportBtn from './resumeBtn/MatchingReportBtn';
import { moveToResults } from '../companyMessage/resultRequest/resultModals';
import MatchMessageVO from '@domain/vo/match/message/MatchMessageVO';
import MatchingMessageListInfoVO from '@domain/vo/match/message/MatchingMessageListInfoVO';
import { CommandTypeCode } from '@domain/rs/match/message/MatchingMessageListRs';
import classnames from 'classnames/bind';
import styles from './userMessage.module.scss';
const cx = classnames.bind(styles);

interface UserMessageProps {
  userMessage: MatchMessageVO;
  messagesInfo: MatchingMessageListInfoVO;
}

const UserMessage = ({ userMessage, messagesInfo }: UserMessageProps) => {
  const { title, createdDayTime, submitApplies, command, sendDateTime, message } = userMessage;
  const isResultRequest = submitApplies.length;
  const positionInfo = useNotificationStore((state) => state.positionInfo);

  if (!positionInfo?.companySn) return null;

  const { beforeOpeningYn } = messagesInfo;

  const getResumeContents = () => {
    switch (command) {
      case CommandTypeCode.WRITING_RESUME:
        return <WritingResumeBtn positionSn={positionInfo.positionSn} beforeOpeningYn={beforeOpeningYn} />;
      case CommandTypeCode.SHOW_SUBMIT_RESUME:
        return (
          <>
            <p className={cx('desc')}>
              지원서 수정은 지원 취소 후에 가능합니다.
              <br />
              단, 지원 취소가 제한되는 상황이 있을 수 있습니다.
            </p>
            <SubmitResumeBtn positionSn={positionInfo.positionSn} />
          </>
        );
      case CommandTypeCode.SHOW_SUBMIT_PROFILE_RESUME:
        return (
          <>
            <div className={cx('descWrapper')}>
              <div className={cx('info')}>ⓘ 알려드립니다.</div>
              <p className={cx('description')}>
                지원서 수정은 지원 취소 후에 가능합니다.
                <br />
                전형이 진행된 경우나 지원 마감 시에는 취소가 불가능 합니다.
              </p>
            </div>
            <ProfileResumeBtn positionSn={positionInfo.positionSn} />
          </>
        );
      case CommandTypeCode.SHOW_MATCHING_PROFILE: {
        // TODO 12월 30일 이후 모든 제안에 프로필이 저장되므로 12월 30일 이후 이 조건은 제거해도 됨
        const matchingProfilePreviewStartDate = new Date('2023-11-30T10:00:00');
        const sendDateTimeDate = new Date(sendDateTime);
        const showProfileButton = matchingProfilePreviewStartDate < sendDateTimeDate;
        if (!showProfileButton) return null;

        return (
          <>
            <div className={cx('descWrapper')}>
              <div className={cx('info')}>ⓘ 알려드립니다.</div>
              <p className={cx('description')}>
                수락한 제안은 다시 취소할 수 없습니다.
                <br />
                프로필 정보가 변경되었다면 인사담당자님께 문의해주시기 바랍니다.
              </p>
            </div>
            <MatchingProfileResumeViewBtn positionSn={positionInfo.positionSn} />
          </>
        );
      }
      case CommandTypeCode.SHOW_MATCHING_REPORT:
        return (
          <>
            <div className={cx('descWrapper')}>
              <div className={cx('message')}>{message}</div>
              <div className={cx('info')}>ⓘ 알려드립니다.</div>
              <p className={cx('description')}>
                매칭 수락 기한이 마감되었거나 제안을 거절한 리포트는 확인할 수 없습니다.
              </p>
            </div>
            <MatchingReportBtn positionSn={positionInfo.positionSn} companySn={positionInfo.companySn} />
          </>
        );
    }
  };

  const messageContents = isResultRequest ? (
    <Link href={moveToResults(submitApplies[0].applyType, submitApplies[0].applySn)} legacyBehavior>
      <a className={cx('result')} target="_blank" role="button">
        제출 결과표 확인하기
      </a>
    </Link>
  ) : (
    getResumeContents()
  );

  return (
    <div className={cx('userMessageArea')}>
      <div className={cx('date')}>{createdDayTime}</div>
      <div className={cx('content')}>
        <div className={cx('title')}>{title}</div>
        {messageContents}
      </div>
    </div>
  );
};

export default UserMessage;
